import { inject, provide, type InjectionKey } from 'vue';
import type { TrackListSourceRef } from '@/composables/api/useTrackListQueueApi';

export interface TrackQueueContext {
    source: TrackListSourceRef;
    label?: string;
    /**
     * How long this list is, when the surface knows — the TRUE length, not how
     * many rows are loaded, so a paged host must pass its total rather than
     * counting what it has rendered.
     *
     * Read for one decision only: a list of one has nothing behind the track you
     * clicked, so the Play button skips the queue request entirely. Fetching
     * anyway was a round trip that could only ever return the row already in
     * hand — and when it failed, it put "Could not load the rest of the list" on
     * screen over a list that had no rest.
     *
     * Leave it undefined when the length isn't known; the button then fetches,
     * which is always correct, just not free.
     */
    knownTotal?: number | null;
}

/**
 * Which list the Play buttons under this subtree belong to.
 *
 * Provided as a GETTER, read at click time. A snapshot would queue whatever the
 * search filter was when the page mounted, so changing the filter and hitting
 * Play would silently queue the previous result set.
 *
 * Provide/inject rather than a prop because the Play button sits four levels
 * down (`TrackCardList` → `TrackCard` → `CoverArtTile` → `PlayTrackButton`) and
 * threading a source prop through all of them would touch components that have
 * nothing to do with queues.
 */
export const TRACK_QUEUE_CONTEXT: InjectionKey<() => TrackQueueContext | null> =
    Symbol('trackQueueContext');

export function provideTrackQueueContext(getContext: () => TrackQueueContext | null): void {
    provide(TRACK_QUEUE_CONTEXT, getContext);
}

/**
 * The queue context for the surface this button is on, or null when it is not a
 * listenable list (Track Matching, the Audio File Issues inspector, a single
 * cover-art tile) — in which case a plain click plays exactly one track.
 */
export function useTrackQueueContext(): () => TrackQueueContext | null {
    // `() => null` here is the default VALUE, not a factory producing one —
    // `inject` only treats a function as a factory when its third argument says
    // so. That is exactly what is wanted (the injected thing IS a getter), but
    // it reads like the other case, so: do not "fix" this by passing `true`.
    return inject(
        TRACK_QUEUE_CONTEXT,
        () => null
    );
}
